/**
 * Judgment Index types — matches the official Judgment Index™ Score Sheet.
 *
 * Models a person's Judgment Index result. Results are entered manually or
 * bulk-uploaded by a certified practitioner; the platform never computes scores.
 *
 * Score sheet structure:
 *   Primary Judgment Indicators — 6 IES scores across 2 parts (Work Side / Self Side)
 *   Balance of Judgment — 6 meters, −25 to +25 (proper balance: −5 to +5)
 *   Primary Judgment Type — 1–13
 *   Optional: 15 detailed sub-indicators per part
 *
 * Advisory only: profiles never alter rankings, scores, weights, or any stored
 * prioritization state. They contextualize a participant's input alongside the
 * cognitive-bias layer.
 */

export type JudgmentProfileSource = 'manual' | 'upload';

/** Band classification for a primary IES score. Matches the 5 zones on the score sheet. */
export type ScoreBand = 'strong' | 'good' | 'attention' | 'developing' | 'needs_work';

/**
 * Band labels entered directly from the practitioner's report — one per IES dimension.
 * When present these override computed band classification so practitioners don't have
 * to rely on reverse-engineered thresholds.
 */
export interface JudgmentIESBands {
  intrinsic: ScoreBand | null;
  extrinsic: ScoreBand | null;
  systemic:  ScoreBand | null;
}

/** Balance meter status. The instrument defines −5 to +5 as properly balanced. */
export type BalanceStatus = 'over' | 'balanced' | 'under';

// ── Primary IES scores ─────────────────────────────────────────────────────────

/** IES (Intrinsic / Extrinsic / Systemic) scores for one part of the assessment. */
export interface JudgmentIES {
  /** Part 1: People/Relational Insight. Part 2: Intrinsic self. */
  intrinsic: number;
  /** Part 1: Work/Tactical Insight. Part 2: Role/Practical self. */
  extrinsic: number;
  /** Part 1: Big Picture/Strategic Insight. Part 2: Self-Direction. */
  systemic: number;
}

/** Balance of Judgment meters, each −25 to +25. Proper balance: −5 to +5. */
export interface JudgmentBalance {
  people: number;   // People/Relationships — Work Side
  work: number;     // Work/Tasks — Work Side
  ideas: number;    // Balance with Ideas — Work Side
  esteem: number;   // Self-Esteem/Self-Confidence — Self Side
  concept: number;  // Self-Concept/Role Satisfaction — Self Side
  image: number;    // Self-Image/Self-Motivation — Self Side
}

// ── Detailed sub-indicators (optional) ────────────────────────────────────────

/** 15 detailed sub-indicators from Part 1 — Work Side. */
export interface WorkSideDetail {
  noticing?: number;            // DIF   — Noticing, General Sensitivity
  tolerance?: number;           // DIM-I — Tolerance of Others & Ideas
  trainability?: number;        // DIM-E — Trainability
  strategicConceptual?: number; // DIM-S — Strategic–Conceptual
  conceptualClarity?: number;   // DIM   — Conceptual Clarity
  realismIdealism?: number;     // DIM%  — Realism–Idealism
  empathy?: number;             // INT-I — Relational Skills, Empathy
  dependability?: number;       // INT-E — Dependability, Reliability
  strategicActual?: number;     // INT-S — Strategic–Actual
  problemSolving?: number;      // INT   — Problem-Solving Ability
  difficultPeople?: number;     // INT%  — Difficult People & Situations
  focus?: number;               // DI    — Focus and Concentration
  followingDirections?: number; // DIS   — Following Directions
  stress?: number;              // AI%   — Stress, Coping, Attitude
  validity?: number;            // RHO   — Validity/Consistency
}

/** 15 detailed sub-indicators from Part 2 — Self Side. */
export interface SelfSideDetail {
  selfRegard?: number;          // DIF   — Self-Regard & Self-Care
  meaningfulness?: number;      // DIM-I — Meaningfulness of Work
  workMorale?: number;          // DIM-E — Value of Work / Morale
  organizational?: number;      // DIM-S — Organizational Ability
  conceptBalance?: number;      // DIM   — Concept Balance
  roleIdentity?: number;        // DIM%  — Role Identity
  personalProblems?: number;    // INT-I — Solving Personal Problems
  practicalProblems?: number;   // INT-E — Solving Practical Problems
  careSurroundings?: number;    // INT-S — Care of Surroundings
  whatIsImportant?: number;     // INT   — Clear on What is Important
  selfCriticism?: number;       // INT%  — Self-Criticism, Hard on Self
  assertiveness?: number;       // DI    — Assertiveness
  moralClarity?: number;        // DIS   — General Moral Clarity
  stress?: number;              // AI%   — Stress, Coping, Attitude
  validity?: number;            // RHO   — Validity/Consistency
}

// ── Profile ────────────────────────────────────────────────────────────────────

export interface JudgmentIndexProfile {
  id: string;
  workspaceId: string;
  subjectUserId?: string | null;
  subjectName: string;
  subjectEmail?: string | null;
  /** ISO date (YYYY-MM-DD) the assessment was completed. */
  assessedOn?: string | null;
  source: JudgmentProfileSource;
  /** Part 1 primary IES scores — Work Side (What You Do). */
  workSide: JudgmentIES;
  /** Part 2 primary IES scores — Self Side (Who You Are). */
  selfSide: JudgmentIES;
  /** 6 Balance of Judgment meters. */
  balance: JudgmentBalance;
  /** Primary Judgment Type number, 1–13. */
  judgmentType?: number | null;
  /** Type name as printed on the report, e.g. "Counseling/Coaching/Mentoring". */
  judgmentTypeName?: string | null;
  /** Band labels entered from the practitioner's report — override computed bands when present. */
  workSideBands?: JudgmentIESBands | null;
  selfSideBands?: JudgmentIESBands | null;
  workSideDetail?: WorkSideDetail | null;
  selfSideDetail?: SelfSideDetail | null;
  notes?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface JudgmentProfileInput {
  subjectUserId?: string | null;
  subjectName: string;
  subjectEmail?: string | null;
  assessedOn?: string | null;
  source?: JudgmentProfileSource;
  workSide: JudgmentIES;
  selfSide: JudgmentIES;
  balance: JudgmentBalance;
  judgmentType?: number | null;
  judgmentTypeName?: string | null;
  workSideBands?: JudgmentIESBands | null;
  selfSideBands?: JudgmentIESBands | null;
  workSideDetail?: WorkSideDetail | null;
  selfSideDetail?: SelfSideDetail | null;
  notes?: string | null;
}

// ── Metadata constants ─────────────────────────────────────────────────────────

export const WORK_SIDE_META: Record<
  keyof JudgmentIES,
  { label: string; description: string; strengthInPrioritization: string; blindSpotInPrioritization: string }
> = {
  intrinsic: {
    label: 'People / Relational Insight',
    description: 'Capacity to read and value people; interpersonal acuity.',
    strengthInPrioritization:
      'Reliably surfaces human impact and stakeholder concerns; attuned to morale and buy-in risk.',
    blindSpotInPrioritization:
      'May under-weight people-affecting items, treating stakeholder cost as a minor factor.',
  },
  extrinsic: {
    label: 'Work / Tactical Insight',
    description: 'Capacity to value tasks, results, and execution; practical planning judgment.',
    strengthInPrioritization:
      'Realistic about effort and feasibility; strong at comparing concrete ROI and execution cost.',
    blindSpotInPrioritization:
      'May mis-estimate effort or ROI; struggles to weigh concrete trade-offs between competing work.',
  },
  systemic: {
    label: 'Big Picture / Strategic Insight',
    description: 'Capacity to think in systems, see the big picture, and reason strategically.',
    strengthInPrioritization:
      'Keeps strategic context in frame; notices how items interact at the portfolio level.',
    blindSpotInPrioritization:
      'May miss systemic interdependencies and strategic compounding value; over-indexes on immediate payoff.',
  },
};

export const SELF_SIDE_META: Record<
  keyof JudgmentIES,
  { label: string; description: string; strengthInPrioritization: string; blindSpotInPrioritization: string }
> = {
  intrinsic: {
    label: 'Self-Worth / Intrinsic Self',
    description: 'Grounded sense of inherent personal value, independent of performance or rank.',
    strengthInPrioritization:
      'Holds an independent position under group pressure; resists deference to authority or groupthink.',
    blindSpotInPrioritization:
      'May defer to louder or higher-status voices, suppressing genuine judgment during ranking.',
  },
  extrinsic: {
    label: 'Role Clarity / Practical Self',
    description: "Clear sense of one's role, responsibilities, and where one's input fits.",
    strengthInPrioritization:
      'Clear about decision rights and scope; contributes where they add value without overreaching.',
    blindSpotInPrioritization:
      'Unclear role sense can blur decision rights — over-stepping scope or withholding warranted input.',
  },
  systemic: {
    label: 'Self-Direction / Systemic Self',
    description: 'Clear sense of purpose, goals, and long-term personal trajectory.',
    strengthInPrioritization:
      'Keeps long-term strategic value in frame; resists trading the important for the merely urgent.',
    blindSpotInPrioritization:
      'Weak long-view can cause over-indexing on short-term urgency and under-weighting of strategic items.',
  },
};

export const BALANCE_META: Record<
  keyof JudgmentBalance,
  { label: string; side: 'work' | 'self'; overMeaning: string; underMeaning: string }
> = {
  people:  {
    label: 'People / Relationships', side: 'work',
    overMeaning: 'May over-weight interpersonal factors relative to results in prioritization.',
    underMeaning: 'May under-weight human impact and stakeholder concerns when ranking.',
  },
  work:    {
    label: 'Work / Tasks', side: 'work',
    overMeaning: 'May be overly task-driven; may prioritize execution over strategy or reflection.',
    underMeaning: 'May under-value concrete deliverables and execution when comparing items.',
  },
  ideas:   {
    label: 'Balance with Ideas', side: 'work',
    overMeaning: 'May lean too abstract or theoretical when evaluating options.',
    underMeaning: 'May miss strategic context and conceptual trade-offs between items.',
  },
  esteem:  {
    label: 'Self-Esteem / Confidence', side: 'self',
    overMeaning: 'High confidence; may resist input from others or override emerging consensus.',
    underMeaning: 'May defer to authority or group; voice may be suppressed in ranking sessions.',
  },
  concept: {
    label: 'Self-Concept / Role', side: 'self',
    overMeaning: 'Strong role identity; may over-restrict input to title or defined scope.',
    underMeaning: 'Unclear role sense; may blur decision rights or withdraw from legitimate input.',
  },
  image:   {
    label: 'Self-Image / Motivation', side: 'self',
    overMeaning: 'High self-motivation; may pursue an agenda independently of group direction.',
    underMeaning: 'May need external validation to contribute confidently to ranking discussions.',
  },
};

export const BAND_META: Record<ScoreBand, { label: string; color: string; chipClass: string; barClass: string }> = {
  strong:     { label: 'Strong',                        color: 'green',  chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200', barClass: 'bg-emerald-500' },
  good:       { label: 'Good',                          color: 'sky',    chipClass: 'bg-sky-50 text-sky-700 border-sky-200',             barClass: 'bg-sky-500'     },
  attention:  { label: 'Needs Attention',               color: 'amber',  chipClass: 'bg-amber-50 text-amber-700 border-amber-200',       barClass: 'bg-amber-500'   },
  developing: { label: 'Needs Development',             color: 'orange', chipClass: 'bg-orange-50 text-orange-700 border-orange-200',    barClass: 'bg-orange-500'  },
  needs_work: { label: 'Needs Significant Development', color: 'rose',   chipClass: 'bg-rose-50 text-rose-700 border-rose-200',          barClass: 'bg-rose-500'    },
};
